// Apps — full pipeline across 5 stages.
// Top-right actions: "+ Add" opens a drawer to add tailored CVs as applications;
// "Clear all" wipes every row (armed two-click).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import Progress from '../components/ui/Progress';
import Drawer from '../components/ui/Drawer';
import ApplicationRow from '../components/domain/ApplicationRow';
import { apiGet, apiPost } from '../lib/api';
import { adaptDataResponse, type NormalizedDataResponse } from '../lib/adapt';
import { useZh } from '../lib/useProduct';
import { stageLabel } from '../lib/stageLabels';
import type { Match, ApplicationStage } from '../lib/types';

type StatsKey = keyof NormalizedDataResponse['stats'];

// The third element used to be a hardcoded bar width (100/70/30/15/8), so the
// funnel drew the same tidy descending shape whatever the data said — an
// account with zero applications still showed a full 已评估 bar tapering to a
// sliver at Offer. The number above each bar was real; the bar was decoration
// in the shape of a chart, which is worse than no chart. Widths are computed
// from the counts now, so the only stage list left to maintain is the labels.
const STAGES: [string, StatsKey][] = [
  ['Evaluated', 'evaluated'],
  ['Applied', 'applied'],
  ['Phone screen', 'phone_screen'],
  ['Interviewing', 'interviews'],
  ['Offer', 'offers'],
];
// Chinese display labels for the funnel — keyed by StatsKey so the underlying
// canonical stage values are never touched.
const STAGE_ZH: Record<string, string> = {
  evaluated: '已评估',
  applied: '已申请',
  phone_screen: '电话初筛',
  interviews: '面试中',
  offers: 'Offer',
};

interface MatchesResponse { total: number; items: Match[] }
interface TailoredItem {
  pdf_file: string;
  pdf_url: string;
  candidate_slug: string | null;
  company_slug: string | null;
  company: string;
  date: string;
  bytes: number;
  mtime: number;
}
interface TailoredResponse { total: number; items: TailoredItem[] }

function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function Apps() {
  const nav = useNavigate();
  const zh = useZh();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });

  const apps = q.data?.applications ?? [];
  const stats = q.data?.stats;

  // Two-click armed Clear all (same pattern as Matches / Scanner / CV writer)
  const [clearArmed, setClearArmed] = useState(false);
  useEffect(() => {
    if (!clearArmed) return;
    const t = setTimeout(() => setClearArmed(false), 3000);
    return () => clearTimeout(t);
  }, [clearArmed]);

  const [addOpen, setAddOpen] = useState(false);

  const clearAll = async () => {
    if (apps.length === 0) return;
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setClearArmed(false);
    try {
      const r = await fetch('/api/applications?confirm=true', {
        method: 'DELETE',
        headers: { 'X-Confirm-Destructive': 'yes' },
      });
      const j = await r.json();
      if (j.ok) {
        qc.invalidateQueries({ queryKey: ['data'] });
      } else {
        alert((zh ? '清空失败：' : 'Clear failed: ') + (j.error || 'unknown'));
      }
    } catch (e) {
      alert((zh ? '清空失败：' : 'Clear failed: ') + (e as Error).message);
    }
  };

  return (
    <div className="page-enter">
      <AddFromTailoredDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existingApps={apps}
      />
      <PageHeader
        eyebrow={zh ? '申请管道 · 全部阶段' : 'Pipeline · all stages'}
        title={zh ? '你走过的每一步。' : "Every move you've made."}
        sub={zh
          ? `${apps.length} 个进行中的申请。点任意一行查看它的评估报告、定制简历和下一步。`
          : `${apps.length} active applications. Click any row to open its report, tailored CV, and next step.`}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={clearAll}
              disabled={apps.length === 0}
              title={clearArmed
                ? (zh ? '再点一次清空所有申请记录' : 'Click again to wipe every row from applications.md')
                : (zh ? '删除所有申请记录（不影响评估报告、定制简历、扫描历史）' : 'Delete every row from applications.md (header preserved). Reports, tailored CVs, and scan history are NOT touched.')}
              style={{
                color: clearArmed ? '#fff' : '#b43a3a',
                background: clearArmed ? '#b43a3a' : 'transparent',
                border: clearArmed ? '1px solid #b43a3a' : undefined,
                transition: 'all 120ms',
              }}
            >
              {clearArmed ? (zh ? '再点一次确认' : 'Click again to confirm') : (zh ? `全部清空 (${apps.length})` : `Clear all (${apps.length})`)}
            </button>
            <button className="btn primary" onClick={() => setAddOpen(true)}>
              {zh ? '+ 添加' : '+ Add'}
            </button>
          </div>
        }
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 24 }}>
        {(() => {
          // Scale every bar against the biggest stage rather than against the
          // first one: a manually added application can put a later stage ahead
          // of 已评估, and dividing by a zero first stage would blow up. All
          // zeros means every bar is empty, which is the honest picture.
          const counts = STAGES.map(([, key]) => (stats?.[key] as number) ?? 0);
          const peak = Math.max(...counts, 0);
          return STAGES.map(([label, key], i) => (
            <div key={label} className="card stat">
              <div className="label">{zh ? (STAGE_ZH[key] ?? label) : label}</div>
              <div className="value" style={{ fontSize: 32 }}>
                {counts[i]}
              </div>
              <Progress
                value={peak > 0 ? (counts[i] / peak) * 100 : 0}
                style={{ marginTop: 12 }}
              />
            </div>
          ));
        })()}
      </div>
      <div className="card">
        <div
          className="row-head"
          style={{ gridTemplateColumns: '1.4fr 1.6fr 1fr 0.7fr 0.85fr 0.85fr 0.65fr 40px' }}
        >
          {(zh
            ? ['公司', '职位', '阶段', '评分', '招聘链接', '日期', '下一步', '']
            : ['Company', 'Role', 'Stage', 'Score', 'Posting', 'Date', 'Next', '']
          ).map((h, i) => (
            <div key={i} className="tab">
              {h}
            </div>
          ))}
        </div>
        {q.isLoading && <div className="empty">{zh ? '加载中…' : 'Loading applications…'}</div>}
        {q.isError && <div className="empty">{zh ? '无法加载申请记录。' : "Couldn't load applications.md."}</div>}
        {q.data && apps.length === 0 && (
          <div className="empty">
            <span className="serif">{zh ? '还没有申请。' : 'No applications yet.'}</span>
            {zh ? '评估一个职位来添加第一条。' : 'Evaluate a job to add your first entry.'}
          </div>
        )}
        {apps.map((a) => (
          <ApplicationRow key={`${a.company}-${a.role}-${a.num}`} app={a} onOpen={(aa) => nav(`/apps/${encodeURIComponent(aa.id || `${aa.num}`)}`)} />
        ))}
      </div>
    </div>
  );
}

// ─── Add from tailored CVs drawer ─────────────────────────────────────────────
// Lists every cv-*.pdf under reports/ that matches a scored role in /api/matches
// and isn't already tracked in applications.md. User picks one or many and
// clicks Add; each becomes a new row with stage="Applied" by default.
function AddFromTailoredDrawer({
  open,
  onClose,
  existingApps,
}: {
  open: boolean;
  onClose: () => void;
  existingApps: Array<{ company: string; role: string }>;
}) {
  const nav = useNavigate();
  const zh = useZh();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  // Tailored ≠ submitted. Default new rows to Evaluated; the user flips to
  // Applied themselves once they actually fill the form on the company site.
  const [stage, setStage] = useState<ApplicationStage>('Evaluated');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ added: number; skipped: number } | null>(null);

  const tailoredQ = useQuery({
    queryKey: ['cv-tailored'],
    queryFn: () => apiGet<TailoredResponse>('/api/cv/tailored'),
    enabled: open,
    staleTime: 10_000,
  });
  const matchesQ = useQuery({
    queryKey: ['matches', { limit: 200 }],
    queryFn: () => apiGet<MatchesResponse>('/api/matches', { limit: 200 }),
    enabled: open,
  });

  // Build a dedupe key from company+role so we can hide tailored rows whose
  // company+role already exists in applications.md.
  const existingKey = useMemo(() => {
    const s = new Set<string>();
    for (const a of existingApps) {
      s.add((a.company || '').toLowerCase() + '|' + (a.role || '').toLowerCase());
    }
    return s;
  }, [existingApps]);

  // Join tailored PDFs → matches on company_slug so we have role + score + URL.
  // Tailored CVs without a matching evaluation (no score) can still be added
  // but show "—" for the role until the user edits.
  const rows = useMemo(() => {
    const matchesBySlug = new Map<string, Match>();
    for (const m of matchesQ.data?.items ?? []) {
      const slug = companySlug(m.company);
      if (slug && !matchesBySlug.has(slug)) matchesBySlug.set(slug, m);
    }
    // Collapse tailored PDFs to one per company_slug (use latest mtime).
    const perCompany = new Map<string, TailoredItem>();
    for (const t of tailoredQ.data?.items ?? []) {
      if (!t.company_slug) continue;
      const prev = perCompany.get(t.company_slug);
      if (!prev || t.mtime > prev.mtime) perCompany.set(t.company_slug, t);
    }
    return [...perCompany.values()].map((t) => {
      const match = matchesBySlug.get(t.company_slug || '');
      const role = match?.role || '';
      const alreadyTracked = existingKey.has(
        (t.company || '').toLowerCase() + '|' + role.toLowerCase(),
      );
      return { tailored: t, match, role, alreadyTracked };
    });
  }, [tailoredQ.data, matchesQ.data, existingKey]);

  const addable = rows.filter((r) => !r.alreadyTracked);
  const allSelected = addable.length > 0 && addable.every((r) => selected[r.tailored.pdf_file]);
  const selectedRows = addable.filter((r) => selected[r.tailored.pdf_file]);

  const toggle = (pdfFile: string) =>
    setSelected((s) => ({ ...s, [pdfFile]: !s[pdfFile] }));

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const next: Record<string, boolean> = {};
      for (const r of addable) next[r.tailored.pdf_file] = true;
      setSelected(next);
    }
  };

  const submit = async () => {
    if (selectedRows.length === 0 || busy) return;
    setBusy(true);
    setResult(null);
    let added = 0, skipped = 0;
    for (const r of selectedRows) {
      try {
        const payload = {
          company: r.tailored.company,
          role: r.role || 'Tailored role',  // fallback so backend validation passes
          stage,
          score: r.match?.score != null ? r.match.score / 20 : undefined,  // 0-100 → 0-5
          url: r.match?.url,
          notes: 'Added from tailored CV',
          tailored_pdf: r.tailored.pdf_file,
          report_file: r.match?.report_file,
          date: r.tailored.date,
        };
        const res = await apiPost<{ merged?: boolean }>('/api/applications', payload);
        if (res) added++;
        else skipped++;
      } catch (_) {
        skipped++;
      }
    }
    setBusy(false);
    setResult({ added, skipped });
    setSelected({});
    qc.invalidateQueries({ queryKey: ['data'] });
  };

  const reset = () => {
    setSelected({});
    setResult(null);
    setStage('Evaluated');
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Drawer open={open} onClose={close} width={620}>
      <div style={{ padding: '22px 24px', borderBottom: '1px solid var(--line)' }}>
        <div className="eyebrow" style={{ marginBottom: 4 }}>{zh ? '添加申请' : 'Add applications'}</div>
        <div className="serif" style={{ fontSize: 26, letterSpacing: '-0.02em' }}>
          {zh ? '从你的定制简历导入。' : 'Pull from your tailored CVs.'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 6 }}>
          {zh
            ? '每一份还没在申请列表里的定制简历都会列在这里。勾选一个或多个，按你选的阶段各自新建一行。'
            : <>Every tailored PDF in <code>reports/</code> that isn't already in your tracker. Pick one or many — each lands as a new row with the stage you choose.</>}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px' }}>
        {(tailoredQ.isLoading || matchesQ.isLoading) && (
          <div style={{ fontSize: 13, color: 'var(--ink3)' }}>{zh ? '加载中…' : 'Loading…'}</div>
        )}

        {!tailoredQ.isLoading && rows.length === 0 && (
          <div className="empty" style={{ padding: '24px 12px' }}>
            <span className="serif">{zh ? '还没有定制简历。' : 'No tailored CVs yet.'}</span>
            {zh ? '先去「简历定制」跑一次，就会出现在这里。' : "Run a tailor pass from the CV writer first — it'll show up here."}
          </div>
        )}

        {rows.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 12, color: 'var(--ink3)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: addable.length ? 'pointer' : 'not-allowed' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={addable.length === 0}
                  onChange={toggleAll}
                  style={{ accentColor: 'var(--pri)' }}
                />
                {zh ? `全选可添加 (${addable.length})` : `Select all addable (${addable.length})`}
              </label>
              <span style={{ marginLeft: 'auto' }}>
                {rows.length - addable.length > 0
                  ? (zh
                      ? `${rows.length - addable.length} 个已在申请列表 — 已隐藏。`
                      : `${rows.length - addable.length} already in tracker — hidden from selection.`)
                  : ''}
              </span>
            </div>

            <div className="card" style={{ padding: 0 }}>
              {rows.map((r) => {
                const on = !!selected[r.tailored.pdf_file];
                return (
                  <div
                    key={r.tailored.pdf_file}
                    onClick={() => !r.alreadyTracked && toggle(r.tailored.pdf_file)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr 0.6fr 60px',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      borderTop: '1px solid var(--line2)',
                      cursor: r.alreadyTracked ? 'not-allowed' : 'pointer',
                      background: on ? 'var(--pri-soft)' : 'transparent',
                      // A 1.12:1 tint cannot carry selection on its own;
                      // an inset left edge marks the row at a glance.
                      boxShadow: on ? 'inset 3px 0 0 var(--pri)' : undefined,
                      opacity: r.alreadyTracked ? 0.45 : 1,
                    }}
                  >
                    <div
                      style={{
                        width: 18, height: 18, borderRadius: 4,
                        border: `1.5px solid ${on ? 'var(--pri)' : 'var(--line)'}`,
                        background: on ? 'var(--pri)' : 'transparent',
                        color: '#fff', fontSize: 11,
                        display: 'grid', placeItems: 'center',
                      }}
                    >
                      {on ? '✓' : ''}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{r.tailored.company}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>
                        {r.role || <em style={{ fontStyle: 'normal' }}>{zh ? '未评估 · 职位未知' : 'No evaluation · role unknown'}</em>}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginTop: 4 }}>
                        {r.tailored.pdf_file}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      {r.alreadyTracked ? (zh ? '已在列表' : 'In tracker') : r.match?.score != null ? (zh ? `匹配 ${r.match.score}` : `Fit ${r.match.score}`) : '—'}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--ink3)', textAlign: 'right' }}>
                      {r.tailored.date}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--line)', padding: '16px 24px', display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="tab">{zh ? '默认阶段' : 'Default stage'}</div>
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as ApplicationStage)}
            style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)' }}
          >
            {(['Evaluated', 'Applied', 'Responded', 'Phone screen', 'Interview', 'Offer'] as ApplicationStage[]).map((s) => (
              <option key={s} value={s}>{stageLabel(s, zh)}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn ghost" onClick={() => { close(); nav('/apps/add'); }} title={zh ? '手动添加（没有定制简历的职位）' : 'Custom freeform add (role without a tailored CV)'}>
          {zh ? '手动添加 →' : 'Custom →'}
        </button>
        <button className="btn ghost" onClick={close}>{zh ? '关闭' : 'Close'}</button>
        <button
          className="btn accent lg"
          disabled={selectedRows.length === 0 || busy}
          onClick={submit}
        >
          {busy ? (zh ? `添加中 ${selectedRows.length}…` : `Adding ${selectedRows.length}…`) : (zh ? `添加 ${selectedRows.length || ''} →` : `Add ${selectedRows.length || ''} →`)}
        </button>
      </div>

      {result && (
        <div
          style={{
            position: 'absolute',
            bottom: 80,
            left: 24,
            right: 24,
            padding: '12px 14px',
            background: result.added > 0 ? 'rgba(45,122,74,0.08)' : 'rgba(180,58,58,0.08)',
            border: `1px solid ${result.added > 0 ? 'rgba(45,122,74,0.3)' : 'rgba(180,58,58,0.3)'}`,
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <strong>{zh ? `已添加 ${result.added} 个申请` : `Added ${result.added} application${result.added === 1 ? '' : 's'}`}</strong>
          {result.skipped > 0 && (
            <span style={{ color: 'var(--ink3)' }}>{zh ? ` · 跳过 ${result.skipped}` : ` · skipped ${result.skipped}`}</span>
          )}
          .{' '}
          <button
            className="btn ghost"
            style={{ fontSize: 11, padding: '2px 8px', marginLeft: 6 }}
            onClick={() => setResult(null)}
          >
            {zh ? '关闭' : 'Dismiss'}
          </button>
        </div>
      )}
    </Drawer>
  );
}
